// Reverse the motor direction on a curtain (13, 51) or a sliding gate (37) —
// which relay means "open".
//
// The installation fact: the two motor leads are the other way round, so Open
// closes and Close opens. The only fix used to be swapping two wires at the
// motor, up a ladder, with the mains off.
//
// ⚠️ THE DEVICE DOES THIS SWAP, NOT THE APP — the opposite of the white light's
// reverse (core/devices/lightReverse), and the difference is the whole reason
// this is a separate module rather than a second flag in that one. The firmware
// has owned motor reversal since before the app could set it:
//
//     OtaForTuya.ino:6246   openRelay  = CurtainMotorReverseAction ? out[1] : out[0];
//     OtaForSwitching.ino:1220  if (ReverseAction == 0) { …DP 0… } else { …DP 2… }
//
// so exchanging OPENCURTAIN and CLOSECURTAIN here as well would CANCEL the
// chip's swap and the curtain would be wrong again — and wrong in a way nobody
// could work out, because each side would be behaving exactly as designed. The
// app sends the direction the user pressed, always, and never translates it.
//
// It also means a wall switch, a schedule, a scene run by the server and Alexa
// are all corrected by the one setting, which an app-side swap could never do.
//
// ── Both places, and the device decides at boot ────────────────────────────
//   `DevicesInstalled.ReverseAction`  0/1 — the authority; read by the firmware
//                                     from the `VerifyDeviceNew` record
//   the chip's own byte               EEPROM[36] on OtaForTuya (marker 0x6B),
//                                     NVS `mot_rev` on the C3 — loaded BEFORE
//                                     any output is driven, so a boot with no
//                                     network is still correct
//
// ⚠️ SO THE COMMAND ALONE IS NOT A SAVE, AND THE COLUMN ALONE IS NOT IMMEDIATE.
// `ReverseAction = doc[0]["ReverseAction"]` yields **0** for a record the device
// cannot read, and the firmware persists that 0 (`ApplyReverseAction(v, true)`),
// so a unit fixed with REVERSEACTIONON alone is un-fixed at the next 20-minute
// poll. And the column on its own does nothing until that poll. Hence both, in
// one action: the column first, then the word.
//
// ⚠️ THE WRITE IS NOT A CORRECTION UNTIL THE DEVICE SAYS SO. The word is sent as
// the SET and the device's own `ReverseAction = …` line is the receipt, so a
// curtain that is powered down reports "saved — the curtain has not confirmed
// it yet" rather than a success it cannot know. Same rule as a door-lock guest
// code (core/devices/lockPasswords).
//
// ⚠️ THE TRAVEL TIMES DO NOT MOVE WITH IT. Open and close have separate seconds
// (core/devices/curtain `KeyPressOffAfter`), and the user still presses "Open"
// — only the relay behind it changes — so the open seconds still belong to the
// open button. An installer who had worked around the reversal by swapping the
// two times must set them back; the dialog says so.

import {
  getDeviceDetail, getDeviceInstalledById, updateDeviceInstalled,
} from '../api/api';
import {
  CMD_REVERSE_ACTION_ON, CMD_REVERSE_ACTION_OFF, CMD_GET_REVERSE_ACTION,
} from './commands';
import { askDeviceFlag, setDeviceFlag } from './deviceFlagProbe';

/** The column, and the name the firmware answers with. */
export const REVERSE_COLUMN = 'ReverseAction';

const cache = new Map();        // uno -> { reversed, ownerId }
const inflight = new Map();     // uno -> Promise
const listeners = new Set();

const emit = (uno, reversed) => listeners.forEach((fn) => {
  try { fn(uno, reversed); } catch (e) { /* a listener's fault */ }
});

export const rowReversed = (row) => Number(row && row[REVERSE_COLUMN]) === 1;

/** Cached flag, or undefined when not read yet. */
export const cachedCurtainReversed = (uno) => cache.get(String(uno))?.reversed;

/** Cached owner (DevicesInstalled.MasterUserId), or undefined. */
export const cachedCurtainOwner = (uno) => cache.get(String(uno))?.ownerId;

/**
 * -> boolean. Never throws; false when it cannot be read.
 *
 * The room-device lists do not carry `ReverseAction`, so it comes off the full
 * install row, once per device, cached for the session — as `SwapWarm` and
 * `GlassFinish` do.
 */
export function getCurtainReversed(device) {
  const uno = String(device && device.UniqueNumber);
  if (cache.has(uno)) return Promise.resolve(cache.get(uno).reversed);
  if (inflight.has(uno)) return inflight.get(uno);
  const p = (async () => {
    try {
      let id = device && device.ID;
      if (id == null) {
        const res = await getDeviceDetail(uno);
        id = (Array.isArray(res.data) ? res.data[0] : null)?.ID;
      }
      if (id == null) return false;
      const res = await getDeviceInstalledById(id);
      const row = Array.isArray(res.data) ? res.data[0] : null;
      const entry = { reversed: rowReversed(row), ownerId: row ? row.MasterUserId : undefined };
      cache.set(uno, entry);
      emit(uno, entry.reversed);
      return entry.reversed;
    } catch (e) {
      return false;
    } finally {
      inflight.delete(uno);
    }
  })();
  inflight.set(uno, p);
  return p;
}

/** Screens re-draw when a flag changes or is first read. -> unsubscribe */
export function onCurtainReverseChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const NO_COLUMN = 'The server does not have the Reverse column for this device yet.';

/**
 * Set it, in both places, and report what the device said.
 *
 * -> { ok, confirmed, message }
 *    ok         the column was written — the setting will survive and will
 *               reach the device at its next check even if it is powered down
 *    confirmed  the device answered with the new value, i.e. it is in the chip
 *               NOW and in its EEPROM
 *
 * ⚠️ THE COLUMN IS WRITTEN FIRST, ON PURPOSE. The firmware persists whatever
 * the server last told it, so sending the word to a device whose column still
 * says the old value leaves the chip correct only until the next poll. Column,
 * then word.
 */
export async function setCurtainReversed(uniqueNumber, reversed, userId) {
  const uno = String(uniqueNumber);
  const want = reversed ? 1 : 0;
  try {
    const res = await updateDeviceInstalled(uno, { [REVERSE_COLUMN]: want });
    const d = res && res.data;
    // The generic update answers a missing column with an error object, not a throw.
    if (d && (d.err || d.status === '500' || d.message === 'some error occured')) {
      return { ok: false, confirmed: false, message: NO_COLUMN };
    }
  } catch (e) {
    const missing = e && e.response && e.response.status === 500;
    return {
      ok: false,
      confirmed: false,
      message: missing ? NO_COLUMN : 'Could not save. Check your connection and try again.',
    };
  }

  cache.set(uno, { ...(cache.get(uno) || {}), reversed: !!reversed });
  emit(uno, !!reversed);

  // Now the device: the word, then the read-back that is the receipt. The SET
  // does not answer with a value — see deviceFlagProbe.setDeviceFlag.
  const word = reversed ? CMD_REVERSE_ACTION_ON : CMD_REVERSE_ACTION_OFF;
  const ack = await setDeviceFlag(uno, word, CMD_GET_REVERSE_ACTION, REVERSE_COLUMN, { userId });
  // ⚠️ A reply carrying the OTHER value is not a confirmation. Firmware that
  // does not know the word can still answer the line (it is published on other
  // events too), and reporting that as success would hide the one case this
  // check exists for.
  const confirmed = ack.answered && ack.value === want;
  return {
    ok: true,
    confirmed,
    // ⚠️ ONE SHORT LINE (owner, 2026-10-26). It is a toast; it has to be read in
    // the two seconds it is up.
    message: confirmed
      ? `${reversed ? 'Reversed' : 'Back to normal'} — the device confirmed it.`
      : 'Saved. The device will pick it up when it is next online.',
  };
}

/**
 * Ask the device what IT holds. -> { answered, value, text }
 *
 * For the bench and for the settings dialog's "device says" line: the column
 * and the chip can legitimately differ for a while (a device that has been off
 * since the change), and showing both is the only way to tell that apart from a
 * firmware that does not have the flag at all.
 */
export const askCurtainReversed = (uniqueNumber, opts) =>
  askDeviceFlag(uniqueNumber, CMD_GET_REVERSE_ACTION, REVERSE_COLUMN, opts);

/** Test hook. */
export const _resetCurtainReverse = () => { cache.clear(); inflight.clear(); };
